import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import type { ExportColumn, ExportFilter, ExportProfile } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { Dialog } from '../components/ui/dialog.js'
import { CheckboxField, SelectField, TextField } from '../components/ui/field.js'
import { Alert } from '../components/ui/feedback.js'
import { format, t } from '../i18n/index.js'
import { cn } from '../lib/cn.js'
import {
  createExportProfile,
  defaultTransform,
  deleteExportProfile,
  downloadExport,
  duplicateExportProfile,
  emptyFilter,
  emptyProfile,
  listAvailableColumns,
  listExportProfiles,
  listExportRuns,
  previewExport,
  repeatExportRun,
  runExport,
  updateExportProfile,
  type AvailableColumns,
  type ExportPreview,
  type ExportProfileInput,
} from '../lib/exports.js'
import { listValueListItems, listValueLists } from '../lib/value-lists.js'

/**
 * The export screen.
 *
 * Choosing columns, seeing what comes out and producing the file are one task,
 * so they live on one screen. Split apart, a profile is built blind and the
 * mistake surfaces in the purchasing system.
 */
export function ExportsPage() {
  const strings = t().exports
  const queryClient = useQueryClient()

  const [editing, setEditing] = useState<ExportProfileInput | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [filter, setFilter] = useState<ExportFilter>(emptyFilter)
  const [duplicating, setDuplicating] = useState<ExportProfile | null>(null)
  const [duplicateName, setDuplicateName] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const profiles = useQuery({ queryKey: ['export-profiles'], queryFn: listExportProfiles })
  const columns = useQuery({ queryKey: ['export-columns'], queryFn: listAvailableColumns })
  const runs = useQuery({ queryKey: ['export-runs'], queryFn: listExportRuns })

  const suppliers = useQuery({
    queryKey: ['export-suppliers'],
    queryFn: async () => {
      const lists = await listValueLists()
      const supplierList = lists.find((list) => list.key === 'suppliers')
      if (!supplierList) return []

      const items = await listValueListItems(supplierList.id, { limit: 200 })
      return items.items
    },
  })

  // Previewed against what is on screen, saved or not — that is the point of
  // showing it while editing. Narrowed into a local so the query function does
  // not have to assert that editing is still set when it runs.
  const previewTarget = editing !== null && editing.columns.length > 0 ? editing : null

  const preview = useQuery({
    queryKey: ['export-preview', previewTarget, filter],
    queryFn: () => previewExport(previewTarget ?? emptyProfile(), filter),
    enabled: previewTarget !== null,
  })

  const save = useMutation({
    mutationFn: (profile: ExportProfileInput) =>
      editingId === null ? createExportProfile(profile) : updateExportProfile(editingId, profile),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['export-profiles'] })
      setNotice(strings.saved)
      setEditing(null)
      setEditingId(null)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const remove = useMutation({
    mutationFn: deleteExportProfile,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['export-profiles'] }),
    onError: (error: Error) => setProblem(error.message),
  })

  const duplicate = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => duplicateExportProfile(id, name),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['export-profiles'] })
      setDuplicating(null)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const produce = useMutation({
    mutationFn: (profileId: string) => runExport(profileId, filter),
    onSuccess: async (result) => {
      downloadExport(result)
      await queryClient.invalidateQueries({ queryKey: ['export-runs'] })

      if (result.unmappableCharacters.length > 0) {
        setProblem(
          format(strings.unmappableWarning, { characters: result.unmappableCharacters.join(' ') }),
        )
      } else if (result.missingPhotos.length > 0) {
        setProblem(format(strings.missingPhotosWarning, { count: result.missingPhotos.length }))
      }
    },
    onError: (error: Error) => setProblem(error.message),
  })

  const repeat = useMutation({
    mutationFn: repeatExportRun,
    onSuccess: (result) => {
      downloadExport(result)
    },
    onError: (error: Error) => setProblem(error.message),
  })

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold">{strings.title}</h1>
        <p className="text-sm text-slate-600">{strings.subtitle}</p>
      </header>

      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {problem !== null && <Alert tone="danger">{problem}</Alert>}

      {editing === null ? (
        <>
          <FilterBar filter={filter} onChange={setFilter} suppliers={suppliers.data ?? []} />

          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{strings.profiles}</h2>
              <Button
                onClick={() => {
                  setEditingId(null)
                  setEditing(emptyProfile())
                }}
              >
                {strings.newProfile}
              </Button>
            </div>

            {profiles.isLoading ? (
              <p className="text-sm text-slate-500">{t().app.loading}</p>
            ) : (profiles.data?.profiles ?? []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
                <p className="font-medium text-slate-700">{strings.noProfiles}</p>
                <p className="mt-1 text-sm text-slate-500">{strings.noProfilesHint}</p>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="export-profiles">
                {(profiles.data?.profiles ?? []).map((profile) => (
                  <li
                    key={profile.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{profile.name}</p>
                      <p className="text-sm text-slate-500">
                        {profile.format.toUpperCase()} · {profile.columns.length} ·{' '}
                        {profile.encoding}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => produce.mutate(profile.id)}
                        disabled={produce.isPending}
                      >
                        {produce.isPending ? strings.running : strings.run}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setEditingId(profile.id)
                          setEditing(toInput(profile))
                        }}
                      >
                        {t().common.edit}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setDuplicating(profile)
                          setDuplicateName(`${profile.name} (Kopie)`)
                        }}
                      >
                        {strings.duplicate}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => {
                          if (window.confirm(strings.deleteProfileConfirm))
                            remove.mutate(profile.id)
                        }}
                      >
                        {t().common.delete}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <RunHistory
            runs={runs.data?.runs ?? []}
            onRepeat={(id) => repeat.mutate(id)}
            isRepeating={repeat.isPending}
          />
        </>
      ) : (
        <ProfileEditor
          profile={editing}
          columns={columns.data}
          preview={preview.data}
          isPreviewLoading={preview.isFetching}
          onChange={setEditing}
          onSave={() => save.mutate(editing)}
          onCancel={() => {
            setEditing(null)
            setEditingId(null)
          }}
          isSaving={save.isPending}
        />
      )}

      <Dialog
        open={duplicating !== null}
        title={strings.duplicate}
        onClose={() => setDuplicating(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDuplicating(null)}>
              {t().common.cancel}
            </Button>
            <Button
              onClick={() => {
                if (duplicating === null) return
                duplicate.mutate({ id: duplicating.id, name: duplicateName })
              }}
              disabled={duplicate.isPending || duplicateName.trim() === ''}
            >
              {strings.duplicate}
            </Button>
          </>
        }
      >
        <TextField
          label={strings.duplicateName}
          value={duplicateName}
          onChange={(event) => setDuplicateName(event.target.value)}
        />
      </Dialog>
    </div>
  )
}

function toInput(profile: ExportProfile): ExportProfileInput {
  const {
    id: _id,
    createdByUserId: _createdBy,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    deletedAt: _deletedAt,
    ...rest
  } = profile

  return rest
}

// --- Filters ----------------------------------------------------------------

function FilterBar({
  filter,
  onChange,
  suppliers,
}: {
  filter: ExportFilter
  onChange: (filter: ExportFilter) => void
  suppliers: { id: string; label: string }[]
}) {
  const strings = t().exports

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">{strings.filters}</h2>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          label={strings.filterSupplier}
          value={filter.supplierItemIds[0] ?? ''}
          options={[
            { value: '', label: strings.allSuppliers },
            ...suppliers.map((supplier) => ({ value: supplier.id, label: supplier.label })),
          ]}
          onChange={(event) =>
            onChange({
              ...filter,
              supplierItemIds: event.target.value === '' ? [] : [event.target.value],
            })
          }
        />

        <TextField
          label={strings.filterCapturedFrom}
          type="date"
          value={filter.capturedFrom?.slice(0, 10) ?? ''}
          onChange={(event) =>
            onChange({
              ...filter,
              capturedFrom:
                event.target.value === '' ? null : `${event.target.value}T00:00:00.000Z`,
            })
          }
        />

        <TextField
          label={strings.filterCapturedTo}
          type="date"
          value={filter.capturedTo?.slice(0, 10) ?? ''}
          onChange={(event) =>
            onChange({
              ...filter,
              capturedTo: event.target.value === '' ? null : `${event.target.value}T23:59:59.999Z`,
            })
          }
        />

        <CheckboxField
          label={strings.filterIncludeNeedsReview}
          checked={filter.includeNeedsReview}
          onChange={(event) => onChange({ ...filter, includeNeedsReview: event.target.checked })}
        />
      </div>
    </section>
  )
}

// --- Editor -----------------------------------------------------------------

interface ProfileEditorProps {
  profile: ExportProfileInput
  columns: AvailableColumns | undefined
  preview: ExportPreview | undefined
  isPreviewLoading: boolean
  onChange: (profile: ExportProfileInput) => void
  onSave: () => void
  onCancel: () => void
  isSaving: boolean
}

function ProfileEditor({
  profile,
  columns,
  preview,
  isPreviewLoading,
  onChange,
  onSave,
  onCancel,
  isSaving,
}: ProfileEditorProps) {
  const strings = t().exports
  const [expanded, setExpanded] = useState<number | null>(null)
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  const usedKeys = useMemo(
    () =>
      new Set(
        profile.columns.map((column) =>
          column.source.kind === 'core'
            ? `core:${column.source.field}`
            : `attr:${column.source.key}`,
        ),
      ),
    [profile.columns],
  )

  function updateColumn(index: number, changes: Partial<ExportColumn>) {
    onChange({
      ...profile,
      columns: profile.columns.map((column, position) =>
        position === index ? { ...column, ...changes } : column,
      ),
    })
  }

  /** Array order is file order, so moving a column moves it in the output. */
  function moveColumn(from: number, to: number) {
    if (to < 0 || to >= profile.columns.length) return

    const reordered = [...profile.columns]
    const [moved] = reordered.splice(from, 1)
    if (moved === undefined) return

    reordered.splice(to, 0, moved)
    onChange({ ...profile, columns: reordered })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{strings.editProfile}</h2>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t().common.cancel}
          </Button>
          <Button onClick={onSave} disabled={isSaving || profile.columns.length === 0}>
            {t().common.save}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label={strings.profileName}
          value={profile.name}
          onChange={(event) => onChange({ ...profile, name: event.target.value })}
        />

        <SelectField
          label={strings.format}
          value={profile.format}
          options={[
            { value: 'csv', label: 'CSV' },
            { value: 'json', label: 'JSON' },
            { value: 'zip', label: 'ZIP' },
          ]}
          onChange={(event) =>
            onChange({ ...profile, format: event.target.value as ExportProfileInput['format'] })
          }
        />
      </div>

      <fieldset className="rounded-lg border border-slate-200 bg-white p-4">
        <legend className="px-2 text-sm font-semibold text-slate-700">{strings.dialect}</legend>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            label={strings.delimiter}
            value={profile.delimiter}
            options={[
              { value: ';', label: 'Semikolon (;)' },
              { value: ',', label: 'Komma (,)' },
              { value: '\t', label: 'Tabulator' },
              { value: '|', label: 'Senkrechter Strich (|)' },
            ]}
            onChange={(event) =>
              onChange({
                ...profile,
                delimiter: event.target.value as ExportProfileInput['delimiter'],
              })
            }
          />

          <SelectField
            label={strings.encoding}
            hint={strings.encodingHint}
            value={profile.encoding}
            options={[
              { value: 'utf-8-bom', label: 'UTF-8 mit BOM' },
              { value: 'utf-8', label: 'UTF-8' },
              { value: 'windows-1252', label: 'Windows-1252' },
            ]}
            onChange={(event) =>
              onChange({
                ...profile,
                encoding: event.target.value as ExportProfileInput['encoding'],
              })
            }
          />

          <SelectField
            label={strings.lineEnding}
            value={profile.lineEnding}
            options={[
              { value: '\r\n', label: 'Windows (CRLF)' },
              { value: '\n', label: 'Unix (LF)' },
            ]}
            onChange={(event) =>
              onChange({
                ...profile,
                lineEnding: event.target.value as ExportProfileInput['lineEnding'],
              })
            }
          />

          <SelectField
            label={strings.quoting}
            value={profile.quoting}
            options={[
              { value: 'minimal', label: strings.quotingMinimal },
              { value: 'always', label: strings.quotingAlways },
            ]}
            onChange={(event) =>
              onChange({ ...profile, quoting: event.target.value as ExportProfileInput['quoting'] })
            }
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-6">
          <CheckboxField
            label={strings.includeHeader}
            checked={profile.includeHeader}
            onChange={(event) => onChange({ ...profile, includeHeader: event.target.checked })}
          />

          {profile.format === 'zip' && (
            <CheckboxField
              label={strings.includePhotos}
              checked={profile.includePhotos}
              onChange={(event) => onChange({ ...profile, includePhotos: event.target.checked })}
            />
          )}
        </div>
      </fieldset>

      <div className="grid gap-6 lg:grid-cols-[1fr_1.5fr]">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">{strings.availableColumns}</h3>

          {columns === undefined ? (
            <p className="text-sm text-slate-500">{t().app.loading}</p>
          ) : (
            <>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                {strings.coreFields}
              </p>
              <ul className="mb-4 space-y-1">
                {columns.core.map((field) => (
                  <li key={field.field}>
                    <button
                      type="button"
                      className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 disabled:opacity-40"
                      disabled={usedKeys.has(`core:${field.field}`)}
                      onClick={() =>
                        onChange({
                          ...profile,
                          columns: [
                            ...profile.columns,
                            {
                              source: { kind: 'core', field: field.field as never },
                              header: field.label,
                              transform: defaultTransform(),
                            },
                          ],
                        })
                      }
                    >
                      + {field.label}
                    </button>
                  </li>
                ))}
              </ul>

              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                {strings.catalogueFields}
              </p>
              <ul className="space-y-1">
                {columns.attributes.map((attribute) => (
                  <li key={attribute.key}>
                    <button
                      type="button"
                      className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-slate-100 disabled:opacity-40"
                      disabled={usedKeys.has(`attr:${attribute.key}`)}
                      onClick={() =>
                        onChange({
                          ...profile,
                          columns: [
                            ...profile.columns,
                            {
                              source: { kind: 'attribute', key: attribute.key },
                              header: attribute.label,
                              transform: defaultTransform(),
                            },
                          ],
                        })
                      }
                    >
                      + {attribute.label}
                      <span className="ml-2 text-xs text-slate-400">{attribute.type}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-700">{strings.selectedColumns}</h3>
          <p className="mb-3 text-xs text-slate-500">{strings.dragHint}</p>

          {profile.columns.length === 0 ? (
            <div className="rounded border border-dashed border-slate-300 p-6 text-center">
              <p className="text-sm font-medium text-slate-600">{strings.noColumns}</p>
              <p className="mt-1 text-xs text-slate-500">{strings.noColumnsHint}</p>
            </div>
          ) : (
            <ol className="space-y-2">
              {profile.columns.map((column, index) => (
                <li
                  key={column.source.kind === 'core' ? column.source.field : column.source.key}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragEnd={() => setDragIndex(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (dragIndex !== null) moveColumn(dragIndex, index)
                    setDragIndex(null)
                  }}
                  className={cn(
                    'rounded border border-slate-200 bg-slate-50 p-2',
                    dragIndex === index && 'opacity-50',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="cursor-grab select-none text-slate-400" aria-hidden="true">
                      ⠿
                    </span>

                    <input
                      aria-label={strings.header}
                      value={column.header}
                      onChange={(event) => updateColumn(index, { header: event.target.value })}
                      className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
                    />

                    {/* Keyboard equivalents: dragging is unreachable without a
                        pointer, and the portal has to stay operable by keyboard. */}
                    <button
                      type="button"
                      aria-label={strings.moveUp}
                      className="px-1 text-slate-500 disabled:opacity-30"
                      disabled={index === 0}
                      onClick={() => moveColumn(index, index - 1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={strings.moveDown}
                      className="px-1 text-slate-500 disabled:opacity-30"
                      disabled={index === profile.columns.length - 1}
                      onClick={() => moveColumn(index, index + 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="px-1 text-sm text-slate-500"
                      onClick={() => setExpanded(expanded === index ? null : index)}
                    >
                      {expanded === index ? '▾' : '▸'}
                    </button>
                    <button
                      type="button"
                      aria-label={strings.removeColumn}
                      className="text-danger px-1"
                      onClick={() => {
                        onChange({
                          ...profile,
                          columns: profile.columns.filter((_, position) => position !== index),
                        })
                        setExpanded(null)
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {expanded === index && (
                    <div className="mt-3 grid gap-3 border-t border-slate-200 pt-3 sm:grid-cols-2">
                      <TextField
                        label={strings.dateFormat}
                        value={column.transform.dateFormat ?? ''}
                        placeholder="dd.MM.yyyy"
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: {
                              ...column.transform,
                              dateFormat: event.target.value || null,
                            },
                          })
                        }
                      />

                      <SelectField
                        label={strings.decimalSeparator}
                        value={column.transform.decimalSeparator}
                        options={[
                          { value: ',', label: 'Komma (12,90)' },
                          { value: '.', label: 'Punkt (12.90)' },
                        ]}
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: {
                              ...column.transform,
                              decimalSeparator: event.target
                                .value as ExportColumn['transform']['decimalSeparator'],
                            },
                          })
                        }
                      />

                      <TextField
                        label={strings.emptyValue}
                        value={column.transform.emptyValue}
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: { ...column.transform, emptyValue: event.target.value },
                          })
                        }
                      />

                      <TextField
                        label={strings.multiValueSeparator}
                        value={column.transform.multiValueSeparator}
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: {
                              ...column.transform,
                              multiValueSeparator: event.target.value,
                            },
                          })
                        }
                      />

                      <TextField
                        label={strings.booleanTrue}
                        value={column.transform.booleanTrue}
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: { ...column.transform, booleanTrue: event.target.value },
                          })
                        }
                      />

                      <TextField
                        label={strings.booleanFalse}
                        value={column.transform.booleanFalse}
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: { ...column.transform, booleanFalse: event.target.value },
                          })
                        }
                      />

                      <SelectField
                        label={strings.valueListRendering}
                        value={column.transform.valueListRendering}
                        options={[
                          { value: 'label', label: strings.renderLabel },
                          { value: 'value', label: strings.renderValue },
                          { value: 'metadata', label: strings.renderMetadata },
                        ]}
                        onChange={(event) =>
                          updateColumn(index, {
                            transform: {
                              ...column.transform,
                              valueListRendering: event.target
                                .value as ExportColumn['transform']['valueListRendering'],
                            },
                          })
                        }
                      />

                      {column.transform.valueListRendering === 'metadata' && (
                        <TextField
                          label={strings.metadataKey}
                          value={column.transform.metadataKey ?? ''}
                          placeholder="supplier_number"
                          onChange={(event) =>
                            updateColumn(index, {
                              transform: {
                                ...column.transform,
                                metadataKey: event.target.value || null,
                              },
                            })
                          }
                        />
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">{strings.preview}</h3>
          {preview !== undefined && (
            <p className="text-xs text-slate-500">
              {format(strings.previewOf, {
                shown: preview.rows.length,
                total: preview.totalMatching,
              })}
            </p>
          )}
        </div>

        {isPreviewLoading ? (
          <p className="text-sm text-slate-500">{t().app.loading}</p>
        ) : preview === undefined || preview.rows.length === 0 ? (
          <p className="text-sm text-slate-500">{strings.previewEmpty}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  {preview.header.map((heading, index) => (
                    <th
                      key={`${heading}-${String(index)}`}
                      className="px-3 py-2 text-left font-medium"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="border-b border-slate-100">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="whitespace-nowrap px-3 py-1.5 text-slate-700">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

// --- History ----------------------------------------------------------------

function RunHistory({
  runs,
  onRepeat,
  isRepeating,
}: {
  runs: {
    id: string
    profileName: string
    status: string
    rowCount: number | null
    fileName: string | null
    createdAt: string
    errorMessage: string | null
  }[]
  onRepeat: (id: string) => void
  isRepeating: boolean
}) {
  const strings = t().exports

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{strings.history}</h2>

      {runs.length === 0 ? (
        <p className="text-sm text-slate-500">{strings.noRuns}</p>
      ) : (
        <ul className="space-y-2" data-testid="export-history">
          {runs.map((run) => (
            <li
              key={run.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{run.fileName ?? run.profileName}</p>
                <p className="text-sm text-slate-500">
                  {new Date(run.createdAt).toLocaleString('de-DE')}
                  {run.rowCount !== null &&
                    ` · ${format(strings.runRows, { count: run.rowCount })}`}
                </p>
                {run.errorMessage !== null && (
                  <p className="text-danger text-sm">{run.errorMessage}</p>
                )}
              </div>

              {run.status === 'succeeded' && (
                <Button
                  variant="secondary"
                  onClick={() => onRepeat(run.id)}
                  disabled={isRepeating}
                  title={strings.repeatHint}
                >
                  {strings.repeat}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
